import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';
import { getErrorMessage } from '../../utils/extractApiError';
import { isCanceledError } from '../requestCancel';
import { RefreshUnavailableError, subscribeSessionRefreshed } from '../refreshOutcome';
import { RETRYABLE_CLIENT_ERROR_STATUSES } from '../httpFailure';
import { MutationErrorChannel } from './mutationErrorChannel';
import { QueryErrorReporting } from './queryErrorReporting';

const MAX_QUERY_RETRIES = 2;
const SESSION_HANDLED_STATUSES = new Set([401, 402]);
const UNKNOWN_QUERY_KEY_TAG = 'unknown';

export function getHttpStatus(error: unknown): number | undefined {
  return axios.isAxiosError(error) ? error.response?.status : undefined;
}

// A 4xx answer is deterministic: the same request will be refused again, so retrying only
// multiplies load and delays the page's own error state. 408/429 are the two client statuses
// that mean "try again later". No status at all (network failure, timeout) and 5xx are transient.
export function isDeterministicClientError(error: unknown): boolean {
  const status = getHttpStatus(error);
  return status !== undefined && status >= 400 && status < 500 && !RETRYABLE_CLIENT_ERROR_STATUSES.has(status);
}

/**
 * The `onRetry` for a failed query's inline alert: offered only when trying again can change the
 * outcome, so a 404 or a validation refusal does not show a button that fails the same way.
 */
export function retryUnlessDeterministic(error: unknown, refetch: () => unknown): (() => void) | undefined {
  return isDeterministicClientError(error) ? undefined : () => { void refetch(); };
}

// 401 ends the session, 402 triggers the billing block, and an unavailable refresh already
// raised its own toast with the session intact: all three are handled globally.
export function isSessionHandledError(error: unknown): boolean {
  if (error instanceof RefreshUnavailableError) return true;
  const status = getHttpStatus(error);
  return status !== undefined && SESSION_HANDLED_STATUSES.has(status);
}

function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  // Each retry would hit a fresh 401 and start another refresh against an endpoint that just
  // said it could not answer (AB#2112).
  if (failureCount >= MAX_QUERY_RETRIES || isCanceledError(error) || error instanceof RefreshUnavailableError) {
    return false;
  }
  return !isDeterministicClientError(error);
}

// Only the first queryKey segment is tagged: the remaining segments carry tenant ids, filters and
// search text, which would explode tag cardinality and put identifiers into Sentry tags.
function queryKeyTag(queryKey: readonly unknown[]): string {
  const head = queryKey[0];
  return typeof head === 'string' && head.length > 0 ? head : UNKNOWN_QUERY_KEY_TAG;
}

/**
 * Builds the app's `QueryClient`, split out of `main.tsx` so the mutation-error
 * wiring can be exercised by a real `QueryClientProvider` in tests (AB#1749's
 * regression test needs the actual `MutationCache.onError` behaviour, not a
 * reimplementation of it) without also importing `main.tsx`'s bootstrap side
 * effects (Sentry.init, zod config, `createRoot(...).render(...)`).
 */
export function createQueryClient(): QueryClient {
  const client = new QueryClient({
    // Read failures surface in each page's own error state; this only makes them observable.
    // Cancellation is normal control flow (effect cleanup, superseded filter), not a failure.
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (isCanceledError(error)) return;
        const status = getHttpStatus(error);
        // Session-handled failures are owned globally; every active query reporting them would
        // only flood Sentry.
        if (isSessionHandledError(error)) return;
        // A query that declares 404 as an expected answer ("no rate for that date") is not a failure.
        if (status === 404 && query.meta?.[QueryErrorReporting.NotFoundExpected]) return;
        const tag = queryKeyTag(query.queryKey);
        Sentry.captureException(error, {
          tags: { query_key: tag },
          // Axios messages ("Request failed with status code N") and the localized network/timeout
          // texts would otherwise merge unrelated queries or split one failure per language.
          fingerprint: ['query', tag, String(status ?? 'network')],
        });
      },
    }),
    // Last-resort safety net for write failures. A mutation without its own `onError` used to
    // fail in complete silence: the spinner stopped, nothing changed, and the screen looked
    // exactly like success. Forgetting the handler is the normal case rather than the
    // exceptional one, so the default has to be "say something" instead of "say nothing".
    //
    // AB#1749: this used to branch on `mutation.options.onError` ("does the mutation handle
    // its own rejection?") which does not match how failures are actually reported in this
    // codebase — most pages catch via `mutateAsync` + try/catch, or read `mutation.isError`,
    // neither of which touches `options.onError`. That let the toast fire *in addition to* a
    // page's own inline Alert, so the user read the same sentence twice. The channel is now an
    // explicit, required declaration (`meta.errorToast` / `meta.errorInline`, enforced by
    // mutationErrorChannelGuard.test.ts) instead of an inferred guess. A mutation that declares
    // neither is still not silent — the point of the original guard is preserved — but the
    // audience shifts from end user to developer: it goes to Sentry + a dev console warning so
    // the gap gets caught before it reaches the user, rather than guessing which UI to show.
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        // The interceptor owns the single toast for this failure; a second one here would repeat it.
        if (error instanceof RefreshUnavailableError) return;
        const meta = mutation.meta;
        if (meta?.[MutationErrorChannel.Toast]) {
          toast.error(getErrorMessage(error));
          return;
        }
        if (meta?.[MutationErrorChannel.Inline]) {
          return;
        }
        const mutationLabel = mutation.options.mutationKey
          ? JSON.stringify(mutation.options.mutationKey)
          : '(no mutationKey)';
        Sentry.captureException(error, {
          extra: { mutationLabel },
        });
        if (import.meta.env.DEV) {
          console.warn(
            `[mutationErrorChannel] ${mutationLabel} rejected without declaring meta.errorToast or meta.errorInline`,
            error,
          );
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        retry: shouldRetryQuery,
      },
      mutations: {
        // TanStack's default networkMode ('online') PAUSES a mutation while
        // onlineManager reports offline: onMutate runs, mutationFn never does, so axios's own
        // timeout and network-error handling never fire and MutationCache.onError stays silent
        // (the UI reads "saving" until the browser comes back). 'always' lets the call reach axios
        // so an offline write fails loudly through the declared meta.errorToast/errorInline
        // channel. Nothing in the app relies on pause-and-resume (no persister, no
        // resumePausedMutations); a mutation that truly needs it must opt back in per call.
        networkMode: 'always',
      },
    },
  });
  // Once a refresh succeeds after the refresh-unavailable toast, re-run what failed for that reason
  // (a query that failed on its own, e.g. a 404, would only fail and report again). A query that
  // is already fetching again (usually the very request whose 401 triggered this refresh) is left
  // alone: refetchQueries would otherwise cancel it and send a duplicate.
  subscribeSessionRefreshed(() => {
    void client.refetchQueries({
      type: 'active',
      predicate: (q) => q.state.error instanceof RefreshUnavailableError && q.state.fetchStatus === 'idle',
    });
  });
  return client;
}
